import ActionDialog from "@dashboard/components/ActionDialog/ActionDialog";
import { type ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton/ConfirmButton";
import {
  type AtticErrorFragment,
  type AtticLocationInput,
  useAtticLocationCreateMutation,
  useAtticLocationDeleteMutation,
  useAtticLocationDetailsQuery,
  useAtticLocationRuleChoicesQuery,
  useAtticLocationUpdateMutation,
} from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { useNotifier } from "@dashboard/hooks/useNotifier/useNotifier";
import { getMutationState } from "@dashboard/misc";
import { Box, Skeleton, Text } from "@saleor/macaw-ui-next";
import { useState } from "react";
import { useIntl } from "react-intl";

import AtticLocationDetailsPage, {
  type AtticLocationFormData,
} from "../components/AtticLocationDetailsPage/AtticLocationDetailsPage";
import { locationMessages } from "../messages";
import { atticLocationListPath, atticLocationUrl } from "../urls";

/**
 * What the form sends. On create, every field; on edit, the same, since the
 * API leaves a field it is not given alone and the form always holds them all.
 */
export const formToLocationInput = (data: AtticLocationFormData): AtticLocationInput => ({
  code: data.code.trim().toUpperCase(),
  name: data.name.trim(),
  kind: data.kind,
  parent: data.parent.trim().toUpperCase() || null,
  isActive: data.isActive,
  notes: data.notes,
  cardCategories: data.cardCategories,
  setCodes: data.setCodes,
  rarities: data.rarities,
  finishes: data.finishes,
  tiers: data.tiers,
});

/** `code` null is the create page; otherwise the location with that code. */
export const AtticLocationDetailsView = ({ code }: { code: string | null }) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();
  const [errors, setErrors] = useState<AtticErrorFragment[]>([]);
  const [saveState, setSaveState] = useState<ConfirmButtonTransitionState>("default");
  const [deleting, setDeleting] = useState(false);
  const { data, loading } = useAtticLocationDetailsQuery({
    displayLoader: true,
    variables: { code: code ?? "" },
    skip: code === null,
  });
  const location = data?.atticLocation ?? null;
  const { data: choicesData } = useAtticLocationRuleChoicesQuery();

  const finish = (refused: AtticErrorFragment[], saved: boolean) => {
    setErrors(refused);
    setSaveState(saved ? "success" : "error");
  };
  const [createLocation] = useAtticLocationCreateMutation({
    disableNestedErrorHandling: true,
    onCompleted: result => {
      const payload = result.atticLocationCreate;
      const refused = payload?.errors ?? [];
      const created = payload?.location;

      finish(refused, !refused.length && !!created);

      if (!refused.length && created) {
        notify({ status: "success", text: intl.formatMessage(locationMessages.created) });
        navigate(atticLocationUrl(created.code), { replace: true });
      }
    },
    onError: () => setSaveState("error"),
  });
  const [updateLocation] = useAtticLocationUpdateMutation({
    disableNestedErrorHandling: true,
    onCompleted: result => {
      const payload = result.atticLocationUpdate;
      const refused = payload?.errors ?? [];
      const saved = payload?.location;

      finish(refused, !refused.length && !!saved);

      if (!refused.length && saved) {
        notify({ status: "success", text: intl.formatMessage(locationMessages.saved) });

        // A new code is a new URL.
        if (saved.code !== code) {
          navigate(atticLocationUrl(saved.code), { replace: true });
        }
      }
    },
    onError: () => setSaveState("error"),
  });

  const [deleteLocation, deleteOpts] = useAtticLocationDeleteMutation({
    disableNestedErrorHandling: true,
    onCompleted: result => {
      const payload = result.atticLocationDelete;
      const refused = payload?.errors ?? [];

      if (refused.length) {
        // The backend's own sentence: what still sits in it, or that it is
        // the holding location.
        notify({ status: "error", text: refused.map(error => error.message).join(" ") });
        setDeleting(false);

        return;
      }

      notify({
        status: "success",
        text: intl.formatMessage(locationMessages.deleted, { code: payload?.code ?? code }),
      });
      navigate(atticLocationListPath, { replace: true });
    },
    onError: () => setDeleting(false),
  });

  if (code !== null && loading && !data) {
    return (
      <Box padding={6}>
        <Skeleton />
      </Box>
    );
  }

  if (code !== null && !location) {
    return (
      <Box padding={6}>
        <Text size={4} data-test-id="attic-location-missing">
          {intl.formatMessage(locationMessages.notFound)}
        </Text>
      </Box>
    );
  }

  return (
    <>
      <AtticLocationDetailsPage
        location={location}
        choices={choicesData?.atticLocationRuleChoices ?? null}
        errors={errors}
        disabled={saveState === "loading"}
        saveButtonBarState={saveState}
        onSubmit={form => {
          setErrors([]);
          setSaveState("loading");

          const input = formToLocationInput(form);

          if (location) {
            updateLocation({ variables: { id: location.id, input } });
          } else {
            createLocation({ variables: { input } });
          }
        }}
        onDelete={location ? () => setDeleting(true) : undefined}
      />
      {location && (
        <ActionDialog
          open={deleting}
          variant="delete"
          title={intl.formatMessage(locationMessages.deleteTitle)}
          confirmButtonState={getMutationState(
            deleteOpts.called,
            deleteOpts.loading,
            deleteOpts.data?.atticLocationDelete?.errors ?? [],
          )}
          onClose={() => setDeleting(false)}
          onConfirm={() => deleteLocation({ variables: { id: location.id } })}
        >
          <Text size={3}>
            {intl.formatMessage(locationMessages.deleteContent, { code: location.code })}
          </Text>
        </ActionDialog>
      )}
    </>
  );
};

export default AtticLocationDetailsView;
